import React, { useEffect, useState, useCallback } from "react";
import { useLocalization } from "../../hooks/useLocalization";
import { useAuth } from "../../hooks/useAuth";
import { policyApi } from "../../services/policyApi";
import { EagleState } from "../../components/EagleState";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { AccessDenied } from "../../components/AccessDenied";

export default function MyPoliciesPage() {
  const { user } = useAuth();
  const { t, language } = useLocalization();
  const [policies, setPolicies] = useState([]);
  const [active, setActive] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);

  const load = useCallback(() => {
    if (user?.employee_id) {
      policyApi.statusByEmployee(user.employee_id)
        .then((r) => { setPolicies(r.data); setAccessDenied(false); })
        .catch((err) => { if (err.response?.status === 403) setAccessDenied(true); });
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const handleAcknowledge = async (policy) => {
    await policyApi.acknowledge(policy.id, `I acknowledge and understand ${policy.title_en}`);
    setActive(null);
    load();
  };

  return (
    <div data-testid="my-policies-page" className="space-y-5">
      {accessDenied ? (
        <AccessDenied resourceLabel={t("nav.myPolicies")} />
      ) : (
      <>
      <PageHelpBanner pageKey="my-policies" title="How to use this page">
        <p>Read each policy in full, then click Acknowledge and confirm - your acknowledgment is permanently recorded with a timestamp.</p>
      </PageHelpBanner>
      <h2 className="text-xl font-bold tracking-tight text-gray-900">{t("nav.myPolicies")}</h2>
      {policies.length > 0 && policies.every((p) => p.acknowledged) && (
        <EagleState variant="success" title={t("eagle.allPoliciesAckTitle")} message={t("eagle.allPoliciesAckMessage")} testId="all-policies-acknowledged" />
      )}
      <div className="space-y-3">
        {policies.map((p) => (
          <div key={p.id} className="bg-white rounded-2xl border border-gray-200 p-5" data-testid={`my-policy-item-${p.id}`}>
            <div className="flex items-center justify-between mb-2">
              <p className="font-semibold text-gray-800 text-sm">{language === "ja" ? p.title_ja : p.title_en}</p>
              {p.acknowledged ? (
                <span className="text-xs font-semibold text-emerald-600">{t("policies.acknowledged")}</span>
              ) : (
                <button
                  data-testid={`acknowledge-policy-${p.id}`}
                  onClick={() => setActive(active === p.id ? null : p.id)}
                  className="text-xs font-semibold px-3 py-1.5 rounded-full bg-blue-600 text-white hover:bg-blue-700 active:scale-95 transition-all"
                >
                  {t("policies.acknowledge")}
                </button>
              )}
            </div>
            {active === p.id && (
              <div className="mt-2 space-y-3">
                <p className="text-xs text-gray-500 whitespace-pre-wrap max-h-40 overflow-y-auto border border-gray-100 rounded-lg p-3">
                  {language === "ja" ? p.content_ja : p.content_en}
                </p>
                <button
                  data-testid={`confirm-acknowledge-${p.id}`}
                  onClick={() => handleAcknowledge(p)}
                  className="w-full py-2 rounded-full bg-emerald-500 text-white text-xs font-semibold hover:bg-emerald-600 active:scale-95 transition-all"
                >
                  {t("common.submit")}
                </button>
              </div>
            )}
          </div>
        ))}
        {policies.length === 0 && <EagleState variant="empty" title={t("eagle.noPoliciesTitle")} message={t("eagle.noPoliciesMessage")} testId="my-policies-empty" />}
      </div>
      </>
      )}
    </div>
  );
}
